import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchAgentByUuid,
  saveOrUpdateAgent,
  deleteAgent,
} from '../../api/AgentApi';
import { fetchAllWorkflows } from '../../api/WorkflowApi';
import './AgentPage.scss';
import { Agent, AgentStatus, Workflow } from '../model';
import { PageProps } from '../main/App';
import AgentForm from './AgentForm';
import AgentFileUpload from './AgentFileUpload';

const emptyAgent: Agent = {
  behaviour: '',
  name: '',
  status: AgentStatus.START,
};

const useAgentData = (uuid?: string) => {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [originalAgent, setOriginalAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isNewAgent = !uuid;

  useEffect(() => {
    if (!isNewAgent) {
      setLoading(true);
      fetchAgentByUuid(uuid)
        .then(data => {
          setAgent(data);
          setOriginalAgent(data);
        })
        .catch(() => setError('Failed to load agent'))
        .finally(() => setLoading(false));
    } else {
      setAgent(emptyAgent);
      setOriginalAgent(emptyAgent);
    }
  }, [uuid, isNewAgent]);

  return {
    agent,
    setAgent,
    originalAgent,
    setOriginalAgent,
    loading,
    error,
    setError,
    isNewAgent,
  };
};

const AgentPage: React.FC<PageProps> = ({ showNotification }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const {
    agent,
    setAgent,
    originalAgent,
    setOriginalAgent,
    loading,
    error,
    setError,
    isNewAgent,
  } = useAgentData(uuid);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [createWorkflow, setCreateWorkflow] = useState(false);

  useEffect(() => {
    setWorkflowsLoading(true);
    fetchAllWorkflows()
      .then(setWorkflows)
      .catch(() => setError('Failed to load workflows'))
      .finally(() => setWorkflowsLoading(false));
  }, [setError]);

  const isAgentChanged =
    JSON.stringify(agent) !== JSON.stringify(originalAgent);
  const isBehaviourValid =
    agent?.behaviour && agent.behaviour.trim().length > 0;
  const isWorkflowValid = createWorkflow || agent?.workflowUuid;
  const isSaveDisabled = !isAgentChanged;
  const isDeleteDisabled = isNewAgent;

  const handleSaveBasicAgent = useCallback(async () => {
    if (!agent) return;

    setShowValidationErrors(true);

    if (!isBehaviourValid) {
      setError('Behaviour is required');
      return;
    }
    if (!isWorkflowValid) {
      setError('Workflow is required');
      return;
    }

    setError(null);
    try {
      let agentToSave = { ...agent };

      if (createWorkflow) {
        // Убираем workflowUuid чтобы бэкенд создал workflow автоматически
        agentToSave.workflowUuid = undefined;
      }

      const savedAgent = await saveOrUpdateAgent(agentToSave);
      setAgent(savedAgent);
      setOriginalAgent(savedAgent);
      setShowValidationErrors(false);
      showNotification('Agent saved successfully');
      navigate(`/agent/${savedAgent.uuid}`);
    } catch (error: unknown) {
      let errorMessage = 'Failed to save basic agent';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }
      setError(errorMessage);
    }
  }, [
    agent,
    isBehaviourValid,
    isWorkflowValid,
    createWorkflow,
    setAgent,
    setOriginalAgent,
    setError,
    showNotification,
    navigate,
  ]);

  const handleDeleteAgent = useCallback(async () => {
    if (!agent?.uuid) return;

    try {
      await deleteAgent(agent.uuid);
      showNotification('Agent deleted successfully');
      navigate('/agents');
    } catch (error: unknown) {
      setError('Failed to delete agent');
    }
  }, [agent, showNotification, navigate, setError]);

  if (loading) {
    return <div className="loading">Loading agent...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="agent-page">
      <AgentForm
        agent={agent}
        workflows={workflows}
        workflowsLoading={workflowsLoading}
        showValidationErrors={showValidationErrors}
        onAgentChange={setAgent}
        onSave={handleSaveBasicAgent}
        onDelete={handleDeleteAgent}
        isNewAgent={isNewAgent}
        isSaveDisabled={isSaveDisabled}
        isDeleteDisabled={isDeleteDisabled}
        loading={loading}
        createWorkflow={createWorkflow}
        onCreateWorkflowChange={setCreateWorkflow}
      />

      {!isNewAgent && agent && <AgentFileUpload agent={agent} />}
    </div>
  );
};

export default AgentPage;
